import { BreakpointObserver } from '@angular/cdk/layout';
import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  share,
  shareReplay,
  startWith,
  takeUntil
} from 'rxjs/operators';

import { NativeAppContextHelpers } from '@sondermind/native-app-interface';

import {
  IBreakpoint,
  CLIENT_FLEX_DEFAULT_BREAKPOINTS,
  mergeFlexAndCustomSonderBreakpoints,
  SONDER_BREAKPOINTS
} from './breakpoints.config';

@Injectable({
  providedIn: 'root'
})
export class BreakpointsService implements OnDestroy {
  suffixes$: Observable<string[]>;
  isTiny$: Observable<boolean>;
  isMobile$: Observable<boolean>;
  isMobileOrTablet$: Observable<boolean>;
  isTopNavBreakpoint$: Observable<boolean>;
  isMatchBuilderSingleColumnBreakpoint$: Observable<boolean>;
  isMatchBuilderMobileBreakpoint$: Observable<boolean>;
  private isModalSubj: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isModal$: Observable<boolean> = this.isModalSubj.asObservable();
  private destroyed = new Subject<void>();
  hasMultipleModals: boolean = false;
  // When ObservableMedia/flex had init gaps, we observed all queries and startWith the current
  // snapshot so the first emission matches the real viewport without waiting for resize.
  // See https://github.com/angular/flex-layout/issues/426 (workaround still useful with CDK only).

  private readonly breakpoints: readonly IBreakpoint[];

  private xsBreakpoint: string;
  private mobileBreakpoint: string;
  private mobileOrTabletBreakpoint: string;
  private topNavBreakpoint: string;
  private activeBreakpoints$: Observable<IBreakpoint[]>;
  private matchBuilderSingleColumnBreakpoint: string;
  private matchBuilderMobileBreakpoint: string;

  constructor(
    private breakpointObserver: BreakpointObserver,
    @Optional() @Inject(SONDER_BREAKPOINTS) sonderBreakpoints: readonly IBreakpoint[] | null,
  ) {
    this.breakpoints = sonderBreakpoints?.length
      ? sonderBreakpoints
      : mergeFlexAndCustomSonderBreakpoints(CLIENT_FLEX_DEFAULT_BREAKPOINTS);

    this.xsBreakpoint = this.mediaQueryForAlias('xs') ?? '';
    this.mobileBreakpoint = this.mediaQueryForAlias('lt-md') ?? '';
    this.mobileOrTabletBreakpoint = this.mediaQueryForAlias('lt-lg') ?? '';
    this.topNavBreakpoint = this.mediaQueryForAlias('top-nav-breakpoint') ?? '';
    this.matchBuilderSingleColumnBreakpoint =
      this.mediaQueryForAlias('match-builder-single-column-breakpoint') ?? '';
    this.matchBuilderMobileBreakpoint =
      this.mediaQueryForAlias('match-builder-mobile-breakpoint') ?? '';

    const allMediaQueries = this.breakpoints.map((b) => b.mediaQuery);
    this.activeBreakpoints$ = this.breakpointObserver.observe(allMediaQueries).pipe(
      map(() => this.currentlyActiveBreakpoints()),
      startWith(this.currentlyActiveBreakpoints()),
      takeUntil(this.destroyed.asObservable()),
      share(),
    );

    this.suffixes$ = this.activeBreakpoints$.pipe(
      map((bps) => bps.map((bp) => bp.alias)),
      startWith(this.currentlyActive()),
      shareReplay(1),
    );

    this.isTiny$ = this.setupBreakpointObservable(this.xsBreakpoint);
    this.isMobile$ = this.setupBreakpointObservable(this.mobileBreakpoint);
    this.isMobileOrTablet$ = this.setupBreakpointObservable(this.mobileOrTabletBreakpoint);
    this.isTopNavBreakpoint$ = this.setupBreakpointObservable(this.topNavBreakpoint);
    this.isMatchBuilderSingleColumnBreakpoint$ = this.setupBreakpointObservable(
      this.matchBuilderSingleColumnBreakpoint
    );
    this.isMatchBuilderMobileBreakpoint$ = this.setupBreakpointObservable(
      this.matchBuilderMobileBreakpoint
    );
  }

  get isMobile(): boolean {
    return this.isMatched(this.mobileBreakpoint);
  }

  get isMobileOrTablet(): boolean {
    return this.isMatched(this.mobileOrTabletBreakpoint);
  }

  get isTopNavBreakpoint(): boolean {
    return this.isMatched(this.topNavBreakpoint);
  }

  get isMatchBuilderSingleColumnBreakpoint(): boolean {
    return this.isMatched(this.matchBuilderSingleColumnBreakpoint);
  }

  get isMatchBuilderMobileBreakpoint(): boolean {
    return this.isMatched(this.matchBuilderMobileBreakpoint);
  }

  ngOnDestroy(): void {
    this.destroyed.next();
  }

  currentlyActive(): string[] {
    return this.currentlyActiveBreakpoints().map((b) => b.alias);
  }

  private mediaQueryForAlias(alias: string): string | undefined {
    return this.breakpoints.find((b) => b.alias === alias)?.mediaQuery;
  }

  private currentlyActiveBreakpoints(): IBreakpoint[] {
    return this.breakpoints
      .filter((b) => b.mediaQuery && this.breakpointObserver.isMatched(b.mediaQuery));
  }

  private isMatched(mediaQuery: string): boolean {
    if (!mediaQuery) {
      return false;
    }
    return this.breakpointObserver.isMatched(mediaQuery);
  }

  private setupBreakpointObservable(breakpointValue: string): Observable<boolean> {
    if (!breakpointValue) {
      return this.activeBreakpoints$.pipe(
        map(() => false),
        startWith(false),
        distinctUntilChanged(),
        shareReplay(1)
      );
    }
    return this.activeBreakpoints$.pipe(
      map(() => this.breakpointObserver.isMatched(breakpointValue)),
      startWith(this.breakpointObserver.isMatched(breakpointValue)),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  toggleModal(isModal: boolean): void {
    if (NativeAppContextHelpers.isHostedInNativeApp()) {
      NativeAppContextHelpers.onModalChanged(isModal);
    }
    this.isModalSubj.next(isModal);
  }
}
